import { createTaskRunner } from '@gonfalon/async/createTaskRunner';
import { chunk } from '@gonfalon/collections/chunk';
import { searchContexts } from '@gonfalon/openapi/operations/searchContexts';
import type { schemas } from '@gonfalon/openapi/types';
import { and, anyOf, exists, toString as queryfilterToString } from '@gonfalon/queryfilter/builder';

import { batchedContextsSearchLimit, contextsSearchSort } from '../migrations/contexts';

import { reactQueryResponseAdapter } from './reactQueryResponseAdapter';

export type ContextsWithNamesTask = {
  projectKey: string;
  environmentKey: string;
  kindKeys: string[];
};

// this is the max results the API will return for a single batch
const MAX_RESULTS_PER_BATCH = 50;

export const queryContextsWithNamesTaskRunner = createTaskRunner<
  ContextsWithNamesTask,
  schemas['Contexts'],
  NonNullable<Awaited<ReturnType<typeof searchContexts>>['data']>
>({
  runner: async (inputs) => {
    return reactQueryResponseAdapter(
      searchContexts({
        projectKey: inputs[0].projectKey,
        environmentKey: inputs[0].environmentKey,
        query: {
          filter: buildFilter(inputs[0].kindKeys),
          sort: contextsSearchSort('-ts'),
          // Batches carry up to 50 kind:keys but the API's default page size
          // is 20, so the batch size must be forwarded to avoid dropping results.
          limit: batchedContextsSearchLimit(MAX_RESULTS_PER_BATCH),
        },
        body: {},
      }),
    );
  },
  resolver: (data) => {
    return {
      _links: data._links,
      _environmentId: data._environmentId,
      continuationToken: data.continuationToken,
      totalCount: data.totalCount,
      items: data.items,
    };
  },
  batching: {
    batcher: queryContextsWithNamesTaskBatcher,
    resultMerger: (results) => {
      return {
        _links: results[0]?._links,
        _environmentId: results[0]?._environmentId,
        items: results.flatMap((result) => result.items || []),
        totalCount: results.reduce((total, result) => total + (result.totalCount ?? 0), 0),
        continuationToken: results[results.length - 1]?.continuationToken,
      };
    },
  },
});

function buildFilter(kindKeys: string[]): string {
  if (kindKeys.length === 0) {
    return '';
  }

  // `exists` is what makes this a different question from the initial lookup: it can
  // resolve an older record of the context that still carries a name.
  return queryfilterToString(and([anyOf('kindkey', kindKeys), exists('*.name', true)]));
}

export function queryContextsWithNamesTaskBatcher(inputs: ContextsWithNamesTask[]) {
  const allKindKeys = inputs.flatMap((input) => input.kindKeys);
  const uniqueKindKeys = Array.from(new Set(allKindKeys));

  const kindKeyBatches = chunk(uniqueKindKeys, MAX_RESULTS_PER_BATCH);

  return kindKeyBatches.map((keyBatch) => [
    {
      projectKey: inputs[0].projectKey,
      environmentKey: inputs[0].environmentKey,
      kindKeys: keyBatch,
    },
  ]);
}
